/**
 * Четыре состояния экрана — одно место соответствия (решение `D121`).
 *
 * Второе такое соответствие расходится с первым молча, и расхождение видит
 * только тот, кто открыл оба экрана подряд.
 *
 * Почему это **отображение**, а не бизнес-правило: соответствие читает
 * машинный код отказа — величину, которую вычислил сервер, — и не знает ни
 * ролей, ни статусов, ни того, что за объект запрашивался. Таблица «роль → что
 * можно» была бы правилом и запрещена; перечень кодов закрыт контрактом, и
 * потому соответствие полно по построению.
 *
 * Отказ прав и отсутствие объекта дают **одно** состояние и **побайтово один**
 * текст: различие между «нельзя» и «нет такого» и есть раскрытие существования
 * чужого объекта.
 *
 * Здесь же живёт показ сообщений сервера по полям формы. Отдельного файла
 * каталог экранов не допускает — его перечень закрыт словарём блока, — а мест
 * показа два: экран входа и экран создания фирмы. Общего у них ровно это:
 * сервер называет поле и пишет текст, клиент кладёт текст туда, куда указали.
 */

import { ApiFailure } from '../api/client';
import type { ErrorCode, ErrorDetail, Money } from '../api/contract';
import { currencyStringKey } from '../i18n';
import type { Strings } from '../i18n';

/** Четыре способа **не показать** содержимое. Пятого значения у перечня нет. */
export type ScreenState = 'loading' | 'empty' | 'error' | 'noAccess';

/**
 * Исход обращения к серверу — то, из чего состояние вычисляется.
 *
 * `done` без пустоты состояния не имеет вовсе: «всё хорошо» ничего не рисует,
 * и заводить для этого пятое значение значило бы называть состоянием пустоту.
 */
export type ScreenOutcome =
  | { readonly kind: 'pending' }
  | { readonly kind: 'done'; readonly empty: boolean }
  | { readonly kind: 'failed'; readonly code: ErrorCode | null };

/**
 * Коды, на которые кабинет отвечает «нет доступа».
 *
 * Их два, и оба ведут в одно место: `FR-WEB-006` требует, чтобы обращение к
 * чужой фирме не раскрывало, существует ли она.
 */
const NO_ACCESS_CODES: readonly ErrorCode[] = ['FORBIDDEN', 'NOT_FOUND'];

function isNoAccess(code: ErrorCode | null): boolean {
  for (const known of NO_ACCESS_CODES) {
    if (known === code) {
      return true;
    }
  }

  return false;
}

/**
 * Состояние экрана по исходу обращения — единственное место соответствия.
 *
 * Отказ без конверта контракта — обрыв связи, стоковая страница фреймворка,
 * посредник, ответивший по-своему — приходит без машинного кода и даёт
 * **ошибку**, а не «нет доступа»: про права сервер в этом случае не отвечал
 * ничего, и сказать человеку обратное значит соврать о причине.
 */
export function screenStateFor(outcome: ScreenOutcome): ScreenState | null {
  switch (outcome.kind) {
    case 'pending':
      return 'loading';
    case 'done':
      return outcome.empty ? 'empty' : null;
    case 'failed':
      return isNoAccess(outcome.code) ? 'noAccess' : 'error';
  }
}

/**
 * Исход отказа просмотра по публичной ссылке — блок `WEB-3.2`.
 *
 * Живёт здесь, а не на странице, по той же причине, что и соответствие выше:
 * отказ опознаётся машинным кодом, и место, где коды названы, в треке одно.
 * Это отображение, а не правило: сколько попыток осталось, на сколько заперта
 * ссылка и жива ли она, решил сервер, а здесь только выбирается, какой экран
 * его ответ покажет. Отказ без конверта контракта — обрыв связи.
 */
export type TrackingRefusal = 'pin' | 'locked' | 'gone' | 'offline' | 'other';

/** Код отказа `422`: сервер судит введённое, а не состояние работы. */
export const VALIDATION_FAILED_CODE: ErrorCode = 'VALIDATION_FAILED';
const TRACKING_PIN_CODES: readonly ErrorCode[] = ['FORBIDDEN', VALIDATION_FAILED_CODE];
const TRACKING_LOCKED_CODE: ErrorCode = 'RATE_LIMITED';
const TRACKING_GONE_CODE: ErrorCode = 'NOT_FOUND';

export function trackingRefusalFor(code: ErrorCode | null): TrackingRefusal {
  if (code === null) {
    return 'offline';
  }

  if (code === TRACKING_GONE_CODE) {
    return 'gone';
  }

  if (code === TRACKING_LOCKED_CODE) {
    return 'locked';
  }

  for (const known of TRACKING_PIN_CODES) {
    if (known === code) {
      return 'pin';
    }
  }

  return 'other';
}

function captionFor(state: ScreenState, strings: Strings): string {
  switch (state) {
    case 'loading':
      return strings.state_loading;
    case 'empty':
      return strings.state_empty;
    case 'error':
      return strings.state_error;
    case 'noAccess':
      return strings.state_no_access;
  }
}

/**
 * Отрисовка состояния: один узел, одна подпись, ни одного числа.
 *
 * Ни машинного кода, ни идентификатора запроса, ни сообщения сервера здесь
 * нет: у «нет доступа» они раскрыли бы ровно то, что состояние скрывает, а у
 * остальных — сказали бы человеку то, что ему нечем использовать.
 *
 * Размеры и цвета сюда не пишутся встроенным стилем: обход таблицы стилей
 * делается одной строкой, и сторож, читающий только файл стилей, к ней слеп.
 */
export function renderScreenState(state: ScreenState, strings: Strings, emptyCaption?: string): HTMLElement {
  const node = document.createElement('p');

  node.className = `screen-state screen-state--${state}`;
  node.setAttribute('role', 'status');
  // Своя подпись бывает только у пустоты списка («объявлений пока нет»): она
  // говорит о пришедшем ответе и ничего не раскрывает. У отказов подпись
  // одна на весь трек, и своей у них нет.
  node.textContent = state === 'empty' && emptyCaption !== undefined ? emptyCaption : captionFor(state, strings);

  return node;
}

/**
 * Где встаёт состояние: на месте **списка** или на месте **экрана** — правило
 * одно на все экраны и живёт одной функцией (долг `TD-66`).
 *
 * Правило было записано трижды — в профиле фирмы, в документах сотрудника и
 * на экране фирмы — и уже разошлось именем различителя (`answered` в двух,
 * `company === null` в третьем). Четвёртый экран получал правило вместе с
 * внимательностью пишущего, а не вместе с кодом.
 *
 * Различитель — **пришёл ли ответ**, а не само состояние. Пустой список и
 * отсутствие ответа неразличимы по списку — он пуст в обоих случаях, — а
 * показывать их надо по-разному. Пустой ответ — ответ пришедший: обрамление
 * экрана (заголовок, ссылки наружу, действия) остаётся, и уйти с экрана есть
 * куда. Ответа нет — незавершённое чтение, отказ прав, отсутствие объекта,
 * обрыв связи — и тогда показывать нечего вовсе: заголовок над «нет доступа»
 * сообщал бы, что объект есть, а это ровно то раскрытие, которого `FR-WEB-006`
 * не допускает. Отказ прав и отсутствие объекта проходят одной веткой и дают
 * побайтово один экран.
 */
export interface StatePlacement {
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  /** Пришёл ли ответ сервера, ради которого экран существует. */
  readonly answered: boolean;
  /** Обрамление: стоит, когда ответ пришёл, — и при пустом списке тоже. */
  readonly frame: readonly HTMLElement[];
  /** Место списка: его занимает состояние, когда ответ пришёл, а показать нечего. */
  readonly list: HTMLElement;
  /** Узлы после места списка — «Показать ещё», вторая часть экрана. */
  readonly after?: readonly HTMLElement[];
  /** Своя подпись пустого списка; без неё — общая подпись «пусто». */
  readonly emptyCaption?: string;
}

export function placeState(element: HTMLElement, placement: StatePlacement, strings: Strings): void {
  const { state, answered, frame, list, after = [], emptyCaption } = placement;

  if (state !== null && !answered) {
    element.replaceChildren(renderScreenState(state, strings));

    return;
  }

  element.replaceChildren(
    ...frame,
    state === null ? list : renderScreenState(state, strings, emptyCaption),
    ...after,
  );
}

/**
 * Подпись значения перечня по ключу, собранному строителем.
 *
 * Живёт рядом с остальным показом, а не в наборах строк: это решение о том,
 * **что показать**, когда показывать нечего, — и принимается оно один раз на
 * все экраны. Значение, которого в наборе нет, — это значение перечисления,
 * появившееся на сервере позже клиента. Ему ставится подпись «неизвестно»
 * **без исходной строки** (CR-007, вариант A): пустое место читается как
 * «поля нет», а машинное значение сервера человеку нечем использовать.
 */
export function enumLabel(strings: Strings, key: keyof Strings): string {
  const found: string | undefined = strings[key];

  return found ?? strings.enum_unknown;
}

/**
 * Сумма денег подписью — блок `WEB-2.1`: сумма строкой, как пришла, и код
 * валюты сервера подписью строителя (решение формы `Б4`). Ни перевода в число,
 * ни округления, ни пересчёта: двоичная дробь на деньгах теряет копейки молча,
 * а курс и сравнение цен клиенту не принадлежат (CR-003 §1).
 */
export function moneyLabel(strings: Strings, money: Money): string {
  return `${money.amount} ${enumLabel(strings, currencyStringKey(money.currency))}`;
}

/**
 * Статус работы пилюлей — блок `WEB-D.1`, решение `Б5`. Принимает ключ строителя
 * `workStatusStringKey`, а не значение: подпись — прежний перевод, текст есть всегда.
 *
 * Модификатор — имя статуса из ключа; какие статусы делят цвет, решает таблица
 * стилей перечнем селекторов. Таблицы вариантов перечня в коде нет (сторож F4), и
 * значение вне перечня получает приглушённый вид с подписью «неизвестно» (CR-007).
 */
export function workStatusPill(strings: Strings, key: keyof Strings): HTMLElement {
  const pill = document.createElement('span');
  const known = (strings[key] as string | undefined) !== undefined;

  pill.className = `status-pill status-pill--${known ? String(key).replace('work_status_', '').toLowerCase() : 'unknown'}`;
  pill.textContent = enumLabel(strings, key);

  return pill;
}

/** Подпись внутреннего заказа рядом с именем и статусом работы — по `Work.internal` (`WEB-3.5`, `Б8`). */
export function workInternalMark(strings: Strings): HTMLElement {
  const mark = document.createElement('span');

  mark.className = 'status-pill work-internal-mark';
  mark.textContent = strings.work_internal;

  return mark;
}

/**
 * Текст отказа **действия** — блок `WEB-2.1`, решение формы `Б6`: публикация,
 * правка, закрытие, одобрение и отклонение идут только онлайн, и отказ
 * показывается текстом на месте действия, а экран остаётся на содержимом.
 *
 * Осмысленный отказ говорит словами сервера: «объявление уже не открыто» и
 * «предложение истекло» знает сервер, и пересказать их клиенту значило бы
 * завести правило (долг `TD-67`). Отказ без конверта контракта — обрыв связи —
 * текста сервера не имеет, и на его месте общая подпись ошибки. Машинного кода
 * и идентификатора запроса в тексте нет ни в каком случае.
 */
export function refusalText(error: unknown, strings: Strings): string {
  if (error instanceof ApiFailure && error.reason !== null) {
    return error.reason;
  }

  return strings.state_error;
}

/**
 * Запись чисел даты: данные `uz` и `tg` в браузерах разные, вплоть до `2026-11-05`,
 * похожего на сырое значение (находка 6 стенда). Все, кроме английского, пишут день точками.
 */
function dateLocale(locale: string): string {
  return locale === 'en' ? locale : 'ru-RU';
}

/**
 * Момент времени подписью локали — показ, а не вычисление.
 *
 * Живёт рядом с остальным показом и по той же причине: решение «как показать
 * момент» принимается один раз на все экраны. Сравнения с текущим временем
 * здесь нет и быть не может: истекло ли окно возражения, решает сервер, и
 * клиент, сравнивший момент со «сейчас», завёл бы правило окна у себя.
 */
export function momentLabel(locale: string, moment: string): string {
  return new Intl.DateTimeFormat(dateLocale(locale)).format(new Date(moment));
}

/**
 * Момент с часами и минутами — блок `WEB-3.2`: на странице отслеживания смена
 * статуса и вехи одного дня различаются только временем. Живёт рядом с датой
 * по той же причине — решение о показе момента одно на трек.
 */
export function momentTimeLabel(locale: string, moment: string): string {
  return new Intl.DateTimeFormat(dateLocale(locale), { dateStyle: 'short', timeStyle: 'short' }).format(new Date(moment));
}

/**
 * Дата без времени — блок `WEB-2.1`: дата погрузки и срок выгрузки приходят
 * днём календаря (`2026-10-24`), а не моментом. Разбор такой строки даёт
 * полночь по Гринвичу, и подпись в поясе западнее Гринвича показала бы
 * вчерашний день; поэтому день показывается в том же поясе, в котором он
 * разобран. Показ, а не вычисление: с текущей датой день не сравнивается.
 */
export function dateLabel(locale: string, day: string): string {
  return new Intl.DateTimeFormat(dateLocale(locale), { timeZone: 'UTC' }).format(new Date(day));
}

/**
 * Отметить в списке выбора значение перечня, пришедшее с сервера.
 *
 * Сравнение идёт **ключом подписи**, а не значением: литерал значения
 * перечисления и голое чтение поля-перечисления в рукописном коде запрещены
 * сторожами трека, а ключ строится единственным строителем и совпадает ровно
 * тогда же, когда совпадает значение. Набор предлагаемых значений этим не
 * трогается: помечается выбранное, а перечень остаётся порождённым целиком.
 */
export function selectEnumOption(
  select: HTMLSelectElement,
  chosenKey: string,
  keyOf: (value: string) => string,
): void {
  for (const option of select.querySelectorAll('option')) {
    option.selected = keyOf(option.value) === chosenKey;
  }
}

/** Класс места, куда ложится сообщение сервера. Назван один раз — здесь. */
export const FIELD_MESSAGE_CLASS = 'field-message';

/** Место под сообщение сервера рядом с полем. Пустое, пока сервер молчит. */
export function createFieldMessageSlot(): HTMLElement {
  const slot = document.createElement('span');

  slot.className = FIELD_MESSAGE_CLASS;

  return slot;
}

/**
 * Сообщения сервера рядом с полями формы, которые он назвал.
 *
 * Сопоставление идёт значением, пришедшим от сервера: имя поля берётся из
 * `detail.field`, элемент ищется по атрибуту `name`, текст кладётся дословно.
 * Ни одного имени поля в коде нет, и ветвления по нему нет тоже — иначе разбор
 * отказа стал бы исполнением чужого правила.
 *
 * Прежние сообщения снимаются целиком перед показом новых: сообщение,
 * пережившее удачную отправку, отвечает на вопрос, которого уже никто не
 * задавал.
 *
 * Наружу отдаётся то, чему места в форме **не нашлось**. Сервер называет поля
 * своего тела, а не элементы экрана, и имя вроде `invitation` формы не имеет
 * вовсе: молча уронив такое сообщение, клиент поглощает отказ сервера — и
 * нажатие выглядит как «ничего не произошло». Что с ним делать, решает экран:
 * здесь только известно, что оно осталось непоказанным.
 */
export function renderFieldMessages(
  form: HTMLElement,
  details: readonly ErrorDetail[],
): readonly ErrorDetail[] {
  for (const slot of form.querySelectorAll(`.${FIELD_MESSAGE_CLASS}`)) {
    slot.textContent = '';
  }

  const homeless: ErrorDetail[] = [];

  for (const detail of details) {
    const field = form.querySelector(`[name="${detail.field}"]`);
    const near = field?.closest('label') ?? field?.parentElement ?? null;
    const slot = near?.querySelector(`.${FIELD_MESSAGE_CLASS}`) ?? null;

    if (slot === null) {
      homeless.push(detail);

      continue;
    }

    slot.textContent = detail.message;
  }

  return homeless;
}
